import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '../../../common/services/snackbar.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../common/components/confirm-dialog/confirm-dialog.component';
import * as _moment from 'moment';
const moment = (_moment as any).default || _moment;

const customFormValidator = (isUnscheduled: boolean): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    return null; // Validation moved to onSave
  };
};

@Component({
  selector: 'app-shift-dialog',
  templateUrl: './shift-dialog.component.html',
  styleUrl: './shift-dialog.component.scss',
  standalone: false
})
export class ShiftDialogComponent implements OnInit {
  form: FormGroup;
  selectedTasks: number[] = [];
  showLimitError = false;
  toastY = 0;

  hours: string[] = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  minutes: string[] = ['00', '15', '30', '45'];

  start_hour: string = '';
  start_min: string = '';
  end_hour: string = '';
  end_min: string = '';
  act_start_hour: string = '';
  act_start_min: string = '';
  act_end_hour: string = '';
  act_end_min: string = '';
  activeTabIndex: number = 0;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<ShiftDialogComponent>,
    private snackBar: SnackbarService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    const shift = data.shift || {};
    const canViewAtt = data?.canViewAttendance !== undefined ? !!data.canViewAttendance : true;
    this.activeTabIndex = (shift.is_unscheduled && canViewAtt) ? 1 : 0;
    if (shift.is_finalized || data.is_submitted || shift.is_readonly) {
      shift.is_readonly = true;
      if (data.shift) {
        data.shift.is_readonly = true;
      }
    }
    
    // Determine initial selected tasks
    this.selectedTasks = [];
    if (shift.task_id1) this.selectedTasks.push(Number(shift.task_id1));
    if (shift.task_id2) this.selectedTasks.push(Number(shift.task_id2));
    if (shift.task_id3) this.selectedTasks.push(Number(shift.task_id3));

    const defaultShop = this.getDefaultShop(data.employee.id, data.date);
    let defaultShopId = (shift.shop_id && Number(shift.shop_id) > 0) ? Number(shift.shop_id) : null;
    if (!defaultShopId) {
        if (data.selectedShopIds && data.selectedShopIds.length > 0) {
            defaultShopId = Number(data.selectedShopIds[0]);
        } else if (defaultShop) {
            defaultShopId = Number(defaultShop.shop_id);
        } else {
            const empShopIds = (data.employee.shop_ids || '').split(',').map((id: string) => Number(id));
            const filteredShops = (data.userShops || []).filter((s: any) => empShopIds.includes(Number(s.id)));
            if (filteredShops.length > 0) {
                defaultShopId = Number(filteredShops[0].id);
            } else if (data.userShops && data.userShops.length > 0) {
                defaultShopId = Number(data.userShops[0].id);
            }
        }
    }

    const isUnscheduled = !!shift.is_unscheduled;
    this.form = this.fb.group({
      id: [shift.id],
      employee_id: [data.employee.id, Validators.required],
      shop_id: [defaultShopId, Validators.required],
      shift_date: [data.date, Validators.required],
      shift_name: [shift.shift_name || ''],
      shift_template_id: [null], // For the dropdown
      start_time: [shift.start_time ? shift.start_time.slice(0, 5) : ''],
      end_time: [shift.end_time ? shift.end_time.slice(0, 5) : ''],
      tasks: [this.selectedTasks],
      is_in_charge: [shift.is_in_charge || false],
      notes: [shift.notes || ''],
      
      // Attendance fields
      act_shift_start_time: [
        shift.act_shift_start_time && shift.act_shift_start_time !== '00:00' && shift.act_shift_start_time !== '00:00:00'
          ? shift.act_shift_start_time.slice(0, 5)
          : ''
      ],
      act_shift_end_time: [
        shift.act_shift_end_time && shift.act_shift_end_time !== '00:00' && shift.act_shift_end_time !== '00:00:00'
          ? shift.act_shift_end_time.slice(0, 5)
          : ''
      ],
      upb: [shift.upb ? shift.upb.slice(0, 5) : '00:00'],
      entry_reason: [this.getInitialEntryReason(shift, data.reasons)],
      entry_reason_others: [shift.entry_reason_others || ''],
      is_absent: [!!shift.is_absent]
    }, { validators: customFormValidator(isUnscheduled) });

    // If editing a scheduled shift, try to find matching template
    if (shift.id && !isUnscheduled) {
        const matchingTemplate = data.masterShifts.find((s: any) => 
            shift.start_time && shift.end_time &&
            s.start_time.slice(0, 5) === shift.start_time.slice(0, 5) && 
            s.end_time.slice(0, 5) === shift.end_time.slice(0, 5)
        );
        if (matchingTemplate) {
            this.form.patchValue({ shift_template_id: matchingTemplate.id });
            this.form.get('start_time')?.disable();
            this.form.get('end_time')?.disable();
        } else {
            this.form.patchValue({ shift_template_id: 'custom' });
        }
    } else if (isUnscheduled) {
        // Unscheduled shift: do not prepopulate roster tab with attendance times
        this.form.patchValue({ 
            shift_template_id: '',
            start_time: '',
            end_time: '',
            shift_name: ''
        });
        this.form.get('start_time')?.disable();
        this.form.get('end_time')?.disable();
    } else {
        // New shift: set default based on date
        if (!this.isPastOrCurrentDate()) {
            if (data.masterShifts && data.masterShifts.length > 0) {
                const defaultTemplate = data.masterShifts[0];
                this.form.patchValue({
                    shift_template_id: defaultTemplate.id,
                    start_time: defaultTemplate.start_time.slice(0, 5),
                    end_time: defaultTemplate.end_time.slice(0, 5),
                    shift_name: defaultTemplate.name
                });
                this.form.get('start_time')?.disable();
                this.form.get('end_time')?.disable();
            } else {
                this.form.patchValue({
                    shift_template_id: 'custom',
                    start_time: '',
                    end_time: '',
                    shift_name: ''
                });
                this.form.get('start_time')?.enable();
                this.form.get('end_time')?.enable();
            }
        } else {
            this.form.patchValue({ 
                shift_template_id: '',
                start_time: '',
                end_time: '',
                shift_name: ''
            });
            this.form.get('start_time')?.disable();
            this.form.get('end_time')?.disable();
        }
    }

    // If external shift from another shop, disable everything
    if (shift.is_readonly) {
        this.form.disable();
    }

    // Initialize dropdown values
    const startParts = this.splitTime(this.form.get('start_time')?.value || '', true);
    this.start_hour = startParts.hour;
    this.start_min = startParts.min;

    const endParts = this.splitTime(this.form.get('end_time')?.value || '', true);
    this.end_hour = endParts.hour;
    this.end_min = endParts.min;

    const actStartParts = this.splitTime(this.form.get('act_shift_start_time')?.value || '', false);
    this.act_start_hour = actStartParts.hour;
    this.act_start_min = actStartParts.min;

    const actEndParts = this.splitTime(this.form.get('act_shift_end_time')?.value || '', false);
    this.act_end_hour = actEndParts.hour;
    this.act_end_min = actEndParts.min;

    // Value changes subscriptions to auto-update local variables
    this.form.get('start_time')?.valueChanges.subscribe(val => {
      const parts = this.splitTime(val, true);
      this.start_hour = parts.hour;
      this.start_min = parts.min;
    });
    this.form.get('end_time')?.valueChanges.subscribe(val => {
      const parts = this.splitTime(val, true);
      this.end_hour = parts.hour;
      this.end_min = parts.min;
    });
    this.form.get('act_shift_start_time')?.valueChanges.subscribe(val => {
      const parts = this.splitTime(val, false);
      this.act_start_hour = parts.hour;
      this.act_start_min = parts.min;
    });
    this.form.get('act_shift_end_time')?.valueChanges.subscribe(val => {
      const parts = this.splitTime(val, false);
      this.act_end_hour = parts.hour;
      this.act_end_min = parts.min;
    });
  }

  private splitTime(time: string, isRoster: boolean = false) {
    if (!time || time === '00:00' || time === '00:00:00') {
      return { hour: '', min: '' };
    }
    const parts = time.split(':');
    if (parts.length >= 2) {
      const h = parts[0].padStart(2, '0');
      const mVal = parseInt(parts[1], 10);
      let m = '00';
      if (isRoster) {
        if (mVal >= 8 && mVal < 23) m = '15';
        else if (mVal >= 23 && mVal < 38) m = '30';
        else if (mVal >= 38) m = '45';
      } else {
        m = parts[1].padStart(2, '0');
      }
      return { hour: h, min: m };
    }
    return { hour: '', min: '' };
  }

  updateStartTime() {
    if (this.start_hour) {
      this.start_hour = this.start_hour.replace(/[^0-9]/g, '');
    }
    if (this.start_min) {
      this.start_min = this.start_min.replace(/[^0-9]/g, '');
    }
    if (!this.start_hour) {
      this.start_min = '';
    }
    const timeVal = (this.start_hour || this.start_min) ? `${this.start_hour || '00'}:${this.start_min || '00'}` : '';
    this.form.get('start_time')?.setValue(timeVal, { emitEvent: false });
    this.form.get('start_time')?.markAsDirty();
  }

  updateEndTime() {
    if (this.end_hour) {
      this.end_hour = this.end_hour.replace(/[^0-9]/g, '');
    }
    if (this.end_min) {
      this.end_min = this.end_min.replace(/[^0-9]/g, '');
    }
    if (!this.end_hour) {
      this.end_min = '';
    }
    const timeVal = (this.end_hour || this.end_min) ? `${this.end_hour || '00'}:${this.end_min || '00'}` : '';
    this.form.get('end_time')?.setValue(timeVal, { emitEvent: false });
    this.form.get('end_time')?.markAsDirty();
  }

  updateActStartTime() {
    if (this.act_start_hour) {
      this.act_start_hour = this.act_start_hour.replace(/[^0-9]/g, '');
      if (this.act_start_hour.length > 2) {
        this.act_start_hour = this.act_start_hour.slice(0, 2);
      }
      const h = parseInt(this.act_start_hour, 10);
      if (!isNaN(h) && h > 23) {
        this.act_start_hour = '23';
      }
    }
    if (this.act_start_min) {
      this.act_start_min = this.act_start_min.replace(/[^0-9]/g, '');
      if (this.act_start_min.length > 2) {
        this.act_start_min = this.act_start_min.slice(0, 2);
      }
      const m = parseInt(this.act_start_min, 10);
      if (!isNaN(m) && m > 59) {
        this.act_start_min = '59';
      }
    }
    if (!this.act_start_hour) {
      this.act_start_min = '';
    }
    const timeVal = (this.act_start_hour || this.act_start_min) ? `${this.act_start_hour || '00'}:${this.act_start_min || '00'}` : '';
    this.form.get('act_shift_start_time')?.setValue(timeVal, { emitEvent: false });
    this.form.get('act_shift_start_time')?.markAsDirty();
  }

  updateActEndTime() {
    if (this.act_end_hour) {
      this.act_end_hour = this.act_end_hour.replace(/[^0-9]/g, '');
      if (this.act_end_hour.length > 2) {
        this.act_end_hour = this.act_end_hour.slice(0, 2);
      }
      const h = parseInt(this.act_end_hour, 10);
      if (!isNaN(h) && h > 23) {
        this.act_end_hour = '23';
      }
    }
    if (this.act_end_min) {
      this.act_end_min = this.act_end_min.replace(/[^0-9]/g, '');
      if (this.act_end_min.length > 2) {
        this.act_end_min = this.act_end_min.slice(0, 2);
      }
      const m = parseInt(this.act_end_min, 10);
      if (!isNaN(m) && m > 59) {
        this.act_end_min = '59';
      }
    }
    if (!this.act_end_hour) {
      this.act_end_min = '';
    }
    const timeVal = (this.act_end_hour || this.act_end_min) ? `${this.act_end_hour || '00'}:${this.act_end_min || '00'}` : '';
    this.form.get('act_shift_end_time')?.setValue(timeVal, { emitEvent: false });
    this.form.get('act_shift_end_time')?.markAsDirty();
  }

  getDefaultShop(employeeId: number, dateStr: string): any {
    if (!this.data.shopEmployees || this.data.shopEmployees.length === 0) {
      return null;
    }
    
    const targetDate = moment(dateStr, 'YYYY-MM-DD');
    const userShopIds = (this.data.userShops || []).map((s: any) => Number(s.id));

    const matches = this.data.shopEmployees.filter((se: any) => {
      if (Number(se.employee_id) !== Number(employeeId)) return false;
      if (Number(se.is_deleted) !== 0) return false;
      if (!se.end_date && Number(se.is_active) !== 1) return false;

      // ONLY consider shops that the logged-in user manages/has access to!
      if (userShopIds.length > 0 && !userShopIds.includes(Number(se.shop_id))) return false;

      // Validate start_date
      if (se.start_date) {
        const start = moment(se.start_date, 'YYYY-MM-DD');
        if (targetDate.isBefore(start, 'day')) return false;
      }

      // Validate end_date
      if (se.end_date) {
        const end = moment(se.end_date, 'YYYY-MM-DD');
        if (targetDate.isAfter(end, 'day')) return false;
      }

      return true;
    });

    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];

    // If more than one record, take is_primary = 1
    const primary = matches.find((se: any) => Number(se.is_primary) === 1);
    return primary || matches[0];
  }

  getFilteredShops() {
    if (!this.data.shopEmployees || this.data.shopEmployees.length === 0) {
      const empShopIds = (this.data.employee.shop_ids || '').split(',').map((id: string) => Number(id));
      return this.data.userShops.filter((s: any) => empShopIds.includes(Number(s.id)));
    }

    const targetDate = moment(this.data.date, 'YYYY-MM-DD');

    const activeShopEmployees = this.data.shopEmployees.filter((se: any) => {
      if (Number(se.employee_id) !== Number(this.data.employee.id)) return false;
      if (Number(se.is_deleted) !== 0) return false;
      if (!se.end_date && Number(se.is_active) !== 1) return false;

      // Validate start_date
      if (se.start_date) {
        const start = moment(se.start_date, 'YYYY-MM-DD');
        if (targetDate.isBefore(start, 'day')) return false;
      }

      // Validate end_date
      if (se.end_date) {
        const end = moment(se.end_date, 'YYYY-MM-DD');
        if (targetDate.isAfter(end, 'day')) return false;
      }

      return true;
    });

    const activeShopIds = activeShopEmployees.map((se: any) => Number(se.shop_id));
    const primaryShopId = activeShopEmployees.find((se: any) => Number(se.is_primary) === 1)?.shop_id;
    
    const filtered = (this.data.userShops || []).filter((s: any) => activeShopIds.includes(Number(s.id)));
    return filtered.sort((a: any, b: any) => {
      const aIsPrimary = Number(a.id) === Number(primaryShopId);
      const bIsPrimary = Number(b.id) === Number(primaryShopId);
      return aIsPrimary ? -1 : (bIsPrimary ? 1 : 0);
    });
  }

  getShopCode(id: number): string {
    if (!id) return '';
    const shop = (this.data.userShops || []).find((s: any) => Number(s.id) === Number(id));
    return shop ? shop.code : '';
  }

  getSelectedShopDisplayValue(): string {
    const shopId = this.form.get('shop_id')?.value;
    if (shopId) {
      const shop = (this.data.userShops || []).find((s: any) => Number(s.id) === Number(shopId));
      if (shop) return `${shop.code} - ${shop.name}`;
    }
    return this.data.shift?.shop_code || '';
  }

  get formattedDate(): string {
    if (!this.data || !this.data.date) {
      return '';
    }
    return moment(this.data.date, 'YYYY-MM-DD').format('ddd D MMM');
  }

  get employeeDisplayName(): string {
    if (!this.data || !this.data.employee) {
      return '';
    }
    const emp = this.data.employee;
    const fName = emp.f_name || '';
    const lName = emp.l_name || '';
    const fullName = (fName + ' ' + lName).trim();
    if (emp.preferred_name && emp.preferred_name.trim()) {
      return `${emp.preferred_name.trim()} - ${fullName}`;
    }
    return fullName;
  }

  isPastOrCurrentDate(): boolean {
    if (!this.data.date) return true; // fallback if date is somehow not provided
    return moment(this.data.date, 'YYYY-MM-DD').isSameOrBefore(moment(), 'day');
  }

  isPastDate(): boolean {
    if (!this.data.date) return false;
    return moment(this.data.date, 'YYYY-MM-DD').isBefore(moment(), 'day');
  }

  isPastWeek(): boolean {
    if (!this.data.date) return false;
    const currentWeekStart = moment().startOf('isoWeek');
    const shiftDate = moment(this.data.date, 'YYYY-MM-DD');
    return shiftDate.isBefore(currentWeekStart, 'day');
  }

  get canViewAttendance(): boolean {
    return this.data?.canViewAttendance !== undefined ? !!this.data.canViewAttendance : true;
  }

  get canEditAttendance(): boolean {
    return this.data?.canEditAttendance !== undefined ? !!this.data.canEditAttendance : true;
  }

  get canViewShift(): boolean {
    return true; // Shift can_view is always true
  }

  get canEditShift(): boolean {
    return this.data?.canEditShift !== undefined ? !!this.data.canEditShift : true;
  }

  get isShiftReadOnly(): boolean {
    return !!(this.data.shift?.is_readonly || !this.canEditShift);
  }

  get isAttendanceReadOnly(): boolean {
    return !!(this.data.shift?.is_readonly || !this.canEditAttendance);
  }

  get isAllReadOnly(): boolean {
    return !!(this.data.shift?.is_readonly || (!this.canEditShift && !this.canEditAttendance));
  }

  ngOnInit(): void {
    if (this.isPastOrCurrentDate() && this.canViewAttendance) {
        this.activeTabIndex = 1;
    } else {
        this.activeTabIndex = 0;
    }

    if (!this.canEditShift || (this.isPastDate() && this.isShiftAssigned)) {
        this.form.get('shift_template_id')?.disable();
        this.form.get('start_time')?.disable();
        this.form.get('end_time')?.disable();
        this.form.get('tasks')?.disable();
        this.form.get('is_in_charge')?.disable();
        this.form.get('notes')?.disable();
    }

    if (!this.canEditAttendance) {
        this.form.get('act_shift_start_time')?.disable();
        this.form.get('act_shift_end_time')?.disable();
        this.form.get('upb')?.disable();
        this.form.get('entry_reason')?.disable();
        this.form.get('entry_reason_others')?.disable();
        this.form.get('is_absent')?.disable();
    }

    // Watch shift template changes
    this.form.get('shift_template_id')?.valueChanges.subscribe(val => {
        if (!this.canEditShift) return;
        if (val === 'custom') {
            this.form.patchValue({ shift_name: '' });
            this.form.get('start_time')?.enable();
            this.form.get('end_time')?.enable();
        } else if (!val || val === '') {
            this.form.patchValue({
                shift_name: '',
                start_time: '',
                end_time: '',
                tasks: []
            });
            this.form.get('start_time')?.disable();
            this.form.get('end_time')?.disable();
        } else if (val) {
            const template = this.data.masterShifts.find((s: any) => s.id === val);
            if (template) {
                this.form.patchValue({
                    start_time: template.start_time.slice(0, 5),
                    end_time: template.end_time.slice(0, 5),
                    shift_name: template.name
                });
                this.form.get('start_time')?.disable();
                this.form.get('end_time')?.disable();
            }
        }
    });
  }

  get warningMessage(): { texts: string[], color: 'red' | 'orange' | 'purple' } | null {
    if (!this.data.shift?.id) return null;
    if (this.data.shift?.is_finalized) return null;
    if (!this.isPastOrCurrentDate()) return null;

    const actStart = this.form.get('act_shift_start_time')?.value;
    const actEnd = this.form.get('act_shift_end_time')?.value;
    const rosterStart = this.form.getRawValue().start_time;
    const rosterEnd = this.form.getRawValue().end_time;
    const hasAttendance = !!(this.data.shift?.attendance_id || 
                             (actStart && actStart !== '' && actStart !== '00:00' && actStart !== '00:00:00') || 
                             (actEnd && actEnd !== '' && actEnd !== '00:00' && actEnd !== '00:00:00'));

    if (this.data.shift?.is_unscheduled) {
       if (!actStart || actStart === '' || actStart === '00:00' || actStart === '00:00:00') return { texts: ['Clock in is missing'], color: 'red' };
       if (!actEnd || actEnd === '' || actEnd === '00:00' || actEnd === '00:00:00') return { texts: ['Clock out is missing'], color: 'red' };
       return { texts: ['Unscheduled shift'], color: 'purple' };
    }

    if (!hasAttendance) {
      const date = this.data.date;
      const today = moment().format('YYYY-MM-DD');
      if (date < today) return { texts: ['Missing attendance'], color: 'red' };
      if (date === today && rosterEnd) {
        if (rosterEnd < moment().format('HH:mm:ss')) return { texts: ['Missing attendance'], color: 'red' };
      }
      return null;
    }

    const isStartMissing = !actStart || actStart === '' || actStart === '00:00' || actStart === '00:00:00';
    const isEndMissing = !actEnd || actEnd === '' || actEnd === '00:00' || actEnd === '00:00:00';

    if (isStartMissing && isEndMissing) return { texts: ['Missing attendance'], color: 'red' };
    if (isStartMissing) return { texts: ['Clock in is missing'], color: 'red' };
    if (isEndMissing) return { texts: ['Clock out is missing'], color: 'red' };

    const msgs: string[] = [];

    const posId = this.data.shift?.pos_id;
    const isManuallyOverridden = (posId !== undefined && posId !== null && Number(posId) === 0);
    const reason = this.form.get('entry_reason')?.value;
    if (isManuallyOverridden || (reason && Number(reason) > 0)) {
        msgs.push('Attendance is manually overridden');
    }

    if (!rosterStart || !rosterEnd) {
        if (msgs.length === 0) {
            return { texts: ['Incomplete roster times'], color: 'orange' };
        }
    } else {
        const getMinutes = (timeStr: string) => {
            if (!timeStr) return null;
            const [hours, minutes] = timeStr.split(':').map(Number);
            return hours * 60 + minutes;
        };

        const isClockInLate = (act: string, rost: string) => {
            const actMins = getMinutes(act);
            const rostMins = getMinutes(rost);
            if (actMins === null || rostMins === null) return false;
            let diff = actMins - rostMins;
            if (diff < -720) diff += 1440;
            if (diff > 720) diff -= 1440;
            return diff > 5;
        };

        const isClockOutEarly = (act: string, rost: string) => {
            const actMins = getMinutes(act);
            const rostMins = getMinutes(rost);
            if (actMins === null || rostMins === null) return false;
            let diff = rostMins - actMins;
            if (diff < -720) diff += 1440;
            if (diff > 720) diff -= 1440;
            return diff > 5;
        };

        if (isClockInLate(actStart, rosterStart) || isClockOutEarly(actEnd, rosterEnd)) {
            msgs.push('Actual time deviates from Rostered by more than 5 minutes');
        }
    }

    if (msgs.length > 0) {
        return { texts: msgs, color: 'orange' };
    }

    return null;
  }

  hasSignificantTimeMismatch(): boolean {
    const start = this.form.getRawValue().start_time;
    const end = this.form.getRawValue().end_time;
    const actStart = this.form.get('act_shift_start_time')?.value;
    const actEnd = this.form.get('act_shift_end_time')?.value;

    const getMinutes = (timeStr: string) => {
        if (!timeStr) return null;
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
    };

    const isDiffSignificant = (t1: number | null, t2: number | null) => {
        if (t1 === null || t2 === null) return false;
        const diff = Math.abs(t1 - t2);
        const shortestDiff = Math.min(diff, 1440 - diff);
        return shortestDiff > 5;
    };

    if (actStart && actStart !== '00:00' && actStart !== '00:00:00' && isDiffSignificant(getMinutes(start), getMinutes(actStart))) return true;
    if (actEnd && actEnd !== '00:00' && actEnd !== '00:00:00' && isDiffSignificant(getMinutes(end), getMinutes(actEnd))) return true;

    return false;
  }

  onTaskSelectionChange(event: any) {
    if (this.isShiftReadOnly) return;
    const list = event.source;
    const changedOption = event.options[0];
    
    if (!changedOption) return;

    const selectedOptions = list.selectedOptions.selected;
    
    const limit = this.getTaskLimit();
    if (selectedOptions.length > limit) {
      // Revert the selection visually
      changedOption.selected = false;

      // Show Toast near the clicked option
      const optionElement = (changedOption as any)._elementRef?.nativeElement;
      if (optionElement) {
        const rect = optionElement.getBoundingClientRect();
        const containerRect = optionElement.closest('.task-selection-container').getBoundingClientRect();
        this.toastY = rect.top - containerRect.top + (rect.height / 2);
      } else {
        this.toastY = 40;
      }

      this.showLimitError = true;
      this.cdr.detectChanges();

      setTimeout(() => {
        this.showLimitError = false;
        this.cdr.detectChanges();
      }, 3000);
    }
    
    // Sync form control with current valid list selection
    const finalValues = list.selectedOptions.selected.map((o: any) => o.value);
    this.form.get('tasks')?.setValue(finalValues, { emitEvent: false });
    this.cdr.detectChanges();
  }

  getTaskLimit(): number {
    return this.data.rosterSettings?.no_of_tasks ? Number(this.data.rosterSettings.no_of_tasks) : 3;
  }

  validateAndGetShiftData(): any {
    const rawValue = this.form.getRawValue();

    if (!rawValue.employee_id) {
        this.showError("Employee ID is required.");
        return null;
    }
    if (!this.data.selectedShopIds?.length && !this.data.employee?.shop_ids) {
        this.showError("Shop is required.");
        return null;
    }

    const start = rawValue.start_time;
    const end = rawValue.end_time;
    const actStart = rawValue.act_shift_start_time;
    const actEnd = rawValue.act_shift_end_time;

    // Validate valid time range (00 to 23 for hour, 00 to 59 for min)
    if (start && !this.isValidTime(start)) {
        this.showError("Please enter a valid From time (Hour 00-23, Minute 00-59).");
        return null;
    }
    if (end && !this.isValidTime(end)) {
        this.showError("Please enter a valid To time (Hour 00-23, Minute 00-59).");
        return null;
    }
    if (actStart && !this.isValidTime(actStart)) {
        this.showError("Please enter a valid Actual From time (Hour 00-23, Minute 00-59).");
        return null;
    }
    if (actEnd && !this.isValidTime(actEnd)) {
        this.showError("Please enter a valid Actual To time (Hour 00-23, Minute 00-59).");
        return null;
    }
    const tasks = rawValue.tasks || [];
    const template = rawValue.shift_template_id;
    const isInCharge = rawValue.is_in_charge;
    const entryReason = rawValue.entry_reason;

    const hasRoster = start || end || tasks.length > 0 || isInCharge || (template && template !== '');
    const hasAttendance = (actStart && actStart !== '' && actStart !== '00:00' && actStart !== '00:00:00') || 
                          (actEnd && actEnd !== '' && actEnd !== '00:00' && actEnd !== '00:00:00') || 
                          entryReason;

    const isAbsent = !!rawValue.is_absent;

    if (!this.isPastOrCurrentDate()) {
        // Future date: roster only
        if (!template || template === '') {
            this.showError('Please select a shift template.');
            return null;
        }
        if (!start || !end) {
            this.showError('Please enter a valid From and To time.');
            return null;
        }
    } else {
        // Past or current week: attendance and roster
        if (!isAbsent) {
            // Cannot clear attendance if it already existed
            if (this.data.shift?.act_shift_start_time && !hasAttendance) {
                this.showError('Cannot clear attendance. Please use the Delete Attendance button to remove it entirely.');
                return null;
            }

            // If actual attendance is present, it must have both clock-in and clock-out
            const originalActStart = this.data.shift?.act_shift_start_time ? this.data.shift.act_shift_start_time.slice(0, 5) : '';
            const originalActEnd = this.data.shift?.act_shift_end_time ? this.data.shift.act_shift_end_time.slice(0, 5) : '';
            const timesChanged = (actStart || '') !== originalActStart || (actEnd || '') !== originalActEnd;

            if (this.activeTabIndex === 1 || actStart || actEnd || hasAttendance) {
                if (!actStart || actStart === '00:00' || actStart === '00:00:00' || 
                    !actEnd || actEnd === '00:00' || actEnd === '00:00:00') {
                    this.showError('Please enter both Actual From (clock-in) and Actual To (clock-out) times.');
                    return null;
                }
                if (timesChanged && !entryReason) {
                    this.showError('Please select an Adjustment Reason for changing the actual attendance times.');
                    return null;
                }
                if (this.isOtherReasonSelected() && (!rawValue.entry_reason_others || rawValue.entry_reason_others.trim() === '')) {
                    this.showError('Please specify the details for the other adjustment reason.');
                    return null;
                }
            }
        }
    }

        // If they enter roster, must be complete
        if (hasRoster) {
            if (!template || template === '') {
                this.showError('Please select a shift template for the roster.');
                return null;
            }
            if (!start || !end) {
                this.showError('Please enter a valid From and To time for the roster.');
                return null;
            }
        }

        // Either shift, attendance, or absent must be there
        if (!hasRoster && !hasAttendance && !isAbsent) {
            this.showError('Please enter either a roster shift, actual attendance, or mark as absent.');
            return null;
        }

    const shiftData = {
      ...rawValue,
      attendance_id: this.data.shift?.attendance_id || null,
      pos_id: this.data.shift?.pos_id !== undefined ? this.data.shift.pos_id : null,
      task_id1: tasks[0] || null,
      task_id2: tasks[1] || null,
      task_id3: tasks[2] || null,
      roster_shop_id: this.data.shift?.roster_shop_id || this.data.selectedShopIds[0] || (this.data.employee.shop_ids ? Number(this.data.employee.shop_ids.split(',')[0]) : null)
    };
    delete shiftData.tasks;
    delete shiftData.shift_template_id;

    if (isAbsent || !hasAttendance) {
      shiftData.act_shift_start_time = '';
      shiftData.act_shift_end_time = '';
      shiftData.entry_reason = null;
      shiftData.entry_reason_others = '';
    } else {
      if (this.isOtherReasonSelected()) {
        shiftData.entry_reason = (this.hasOtherInReasons && typeof this.otherReasonId === 'number') ? this.otherReasonId : null;
        shiftData.entry_reason_others = rawValue.entry_reason_others || '';
      } else {
        shiftData.entry_reason = rawValue.entry_reason ? Number(rawValue.entry_reason) : null;
        shiftData.entry_reason_others = '';
      }
    }

    if (shiftData.start_time && shiftData.end_time) {
      shiftData.is_unscheduled = false;
    } else if (shiftData.act_shift_start_time || shiftData.act_shift_end_time) {
      shiftData.is_unscheduled = true;
    } else {
      shiftData.is_unscheduled = !!this.data.shift?.is_unscheduled;
    }

    // Format times to H:i
    if (shiftData.start_time) shiftData.start_time = this.formatTime(shiftData.start_time);
    if (shiftData.end_time) shiftData.end_time = this.formatTime(shiftData.end_time);
    if (shiftData.act_shift_start_time) shiftData.act_shift_start_time = this.formatTime(shiftData.act_shift_start_time);
    if (shiftData.act_shift_end_time) shiftData.act_shift_end_time = this.formatTime(shiftData.act_shift_end_time);

    return shiftData;
  }

  onSave() {
    const shiftData = this.validateAndGetShiftData();
    if (!shiftData) return;

    const rawValue = this.form.getRawValue();
    const actStart = rawValue.act_shift_start_time;
    const actEnd = rawValue.act_shift_end_time;
    const entryReason = rawValue.entry_reason;
    const hasAttendance = (actStart && actStart !== '' && actStart !== '00:00' && actStart !== '00:00:00') || 
                          (actEnd && actEnd !== '' && actEnd !== '00:00' && actEnd !== '00:00:00') || 
                          entryReason;

    if (hasAttendance && this.hasSignificantTimeMismatch()) {
      const confirmData = new ConfirmDialogModel("Warning", "Warning: Actual attendance time differs from roster shift time by more than 5 minutes. Do you want to proceed?");
      confirmData.yesButtonCaption = "Proceed";
      confirmData.noButtonCaption = "Cancel";

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '400px',
        data: confirmData
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.executeSave(shiftData);
        }
      });
    } else {
      this.executeSave(shiftData);
    }
  }

  private executeSave(shiftData: any) {
    this.dialogRef.close({ action: 'save', shift: shiftData });
  }

  private showError(message: string) {
    const dialogData = new ConfirmDialogModel("Validation Error", message);
    dialogData.showNoButton = false;
    dialogData.yesButtonCaption = "OK";
    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });
  }

  private formatTime(time: string): string {
    if (!time) return time;
    const parts = time.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return time;
  }

  private isValidTime(timeStr: string): boolean {
    if (!timeStr) return true;
    const parts = timeStr.split(':');
    if (parts.length !== 2) return false;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || h < 0 || h > 23) return false;
    if (isNaN(m) || m < 0 || m > 59) return false;
    return true;
  }

  onDelete() {
    if (this.form.dirty) {
      const shiftData = this.validateAndGetShiftData();
      if (!shiftData) return;
      this.dialogRef.close({ action: 'save_then_delete', id: this.form.getRawValue().id, shift: shiftData });
    } else {
      this.dialogRef.close({ action: 'delete', id: this.form.getRawValue().id });
    }
  }

  onDeleteShift() {
    if (this.form.dirty) {
      const shiftData = this.validateAndGetShiftData();
      if (!shiftData) return;
      this.dialogRef.close({ action: 'save_then_delete_shift', id: this.form.getRawValue().id, shift: shiftData });
    } else {
      this.dialogRef.close({ action: 'delete_shift', id: this.form.getRawValue().id });
    }
  }

  onDeleteAttendance() {
    if (this.form.dirty) {
      const shiftData = this.validateAndGetShiftData();
      if (!shiftData) return;
      this.dialogRef.close({ action: 'save_then_delete_attendance', id: this.form.getRawValue().id, shift: shiftData });
    } else {
      this.dialogRef.close({ action: 'delete_attendance', id: this.form.getRawValue().id });
    }
  }

  onFinalize() {
    if (this.form.dirty) {
      const shiftData = this.validateAndGetShiftData();
      if (!shiftData) return;
      this.dialogRef.close({ action: 'save_then_finalize', shift: shiftData });
    } else {
      this.dialogRef.close({ action: 'finalize', shift: this.data.shift });
    }
  }

  onUndoFinalize() {
    if (this.form.dirty) {
      const shiftData = this.validateAndGetShiftData();
      if (!shiftData) return;
      this.dialogRef.close({ action: 'save_then_undo_finalize', shift: shiftData });
    } else {
      this.dialogRef.close({ action: 'undo_finalize', shift: this.data.shift });
    }
  }

  get showDeleteShiftButton(): boolean {
    if (!this.canEditShift) return false;
    const shift = this.data.shift;
    if (!shift || !shift.id || shift.is_readonly || shift.is_unscheduled || shift.is_deleted || shift.is_shift_deleted) {
      return false;
    }
    // Must have a saved scheduled shift
    if (!shift.start_time || !shift.end_time) {
      return false;
    }
    // Delete shift is disabled on past dates
    if (this.isPastDate()) {
      return false;
    }
    return true;
  }

  get showDeleteAttendance(): boolean {
    if (!this.canViewAttendance || !this.canEditAttendance) return false;
    if (this.data.shift?.is_readonly) return false;
    if (this.data.shift?.is_attendance_deleted) return false;
    if (this.form.get('is_absent')?.value || this.data.shift?.is_absent) return false;

    const shift = this.data.shift;
    if (!shift) return false;

    // Only show if the shift already had saved attendance prior to editing
    const hasSavedAttendance = !!(
      shift.attendance_id ||
      (shift.act_shift_start_time && shift.act_shift_start_time !== '' && shift.act_shift_start_time !== '00:00' && shift.act_shift_start_time !== '00:00:00') ||
      (shift.act_shift_end_time && shift.act_shift_end_time !== '' && shift.act_shift_end_time !== '00:00' && shift.act_shift_end_time !== '00:00:00')
    );

    return hasSavedAttendance;
  }

  get showFinalizeButton(): boolean {
    if (!this.canViewAttendance || !this.canEditAttendance) return false;
    if (this.data.shift?.is_readonly || this.data.shift?.is_finalized) return false;
    if (this.data.shift?.is_attendance_deleted) return false;
    if (!this.isPastOrCurrentDate()) return false;

    // Absent means no attendance record to finalize
    if (this.form.get('is_absent')?.value || this.data.shift?.is_absent) {
      return false;
    }

    const shift = this.data.shift;
    if (!shift) return false;

    // Only show finalize button if attendance was already saved
    const hasSavedAttendance = !!(
      shift.attendance_id ||
      (shift.act_shift_start_time && shift.act_shift_start_time !== '' && shift.act_shift_start_time !== '00:00' && shift.act_shift_start_time !== '00:00:00') ||
      (shift.act_shift_end_time && shift.act_shift_end_time !== '' && shift.act_shift_end_time !== '00:00' && shift.act_shift_end_time !== '00:00:00')
    );

    if (!hasSavedAttendance) {
      return false;
    }

    const actStart = this.form.get('act_shift_start_time')?.value;
    const actEnd = this.form.get('act_shift_end_time')?.value;

    const hasValidActualTimes = !!(
      actStart && actStart !== '' && actStart !== '00:00' && actStart !== '00:00:00' &&
      actEnd && actEnd !== '' && actEnd !== '00:00' && actEnd !== '00:00:00' &&
      this.act_start_hour !== '' && this.act_start_min !== '' &&
      this.act_end_hour !== '' && this.act_end_min !== ''
    );

    if (!hasValidActualTimes) {
      return false;
    }

    // If user has selected an adjustment reason, the exception is reviewed and resolved!
    const reason = this.form.get('entry_reason')?.value;
    const isReviewed = !!(reason && (Number(reason) > 0 || reason === 'other' || this.isOtherReasonSelected()));
    if (isReviewed) {
      return true;
    }

    // If not reviewed, check if there are exceptions (unresolved warnings)
    const rosterStart = this.form.getRawValue().start_time;
    const rosterEnd = this.form.getRawValue().end_time;

    if (this.data.shift?.is_unscheduled || !rosterStart || !rosterEnd) {
      return false;
    }

    const posId = this.data.shift?.pos_id;
    const isManuallyOverridden = (posId !== undefined && posId !== null && Number(posId) === 0);
    if (isManuallyOverridden) {
      return false;
    }

    const inBefore = Number(this.data.settingsColors?.att_threshold_in_before ?? 7);
    const inAfter = Number(this.data.settingsColors?.att_threshold_in_after ?? 7);
    const outBefore = Number(this.data.settingsColors?.att_threshold_out_before ?? 7);
    const outAfter = Number(this.data.settingsColors?.att_threshold_out_after ?? 7);

    const getMinutes = (timeStr: string) => {
      if (!timeStr) return null;
      const parts = timeStr.split(':');
      if (parts.length < 2) return null;
      return Number(parts[0]) * 60 + Number(parts[1]);
    };

    const isClockInOutside = (act: string, rost: string) => {
      const actMins = getMinutes(act);
      const rostMins = getMinutes(rost);
      if (actMins === null || rostMins === null) return false;
      let diff = actMins - rostMins;
      if (diff < -720) diff += 1440;
      if (diff > 720) diff -= 1440;
      return diff < -inBefore || diff > inAfter;
    };

    const isClockOutOutside = (act: string, rost: string) => {
      const actMins = getMinutes(act);
      const rostMins = getMinutes(rost);
      if (actMins === null || rostMins === null) return false;
      let diff = actMins - rostMins;
      if (diff < -720) diff += 1440;
      if (diff > 720) diff -= 1440;
      return diff < -outBefore || diff > outAfter;
    };

    if (isClockInOutside(actStart, rosterStart) || isClockOutOutside(actEnd, rosterEnd)) {
      return false;
    }

    return true;
  }

  get showAbsentCheckbox(): boolean {
    if (!this.isShiftAssigned) return false;

    const actStart = this.form.get('act_shift_start_time')?.value;
    const actEnd = this.form.get('act_shift_end_time')?.value;

    if (actStart && actStart !== '' && actStart !== '00:00' && actStart !== '00:00:00') return false;
    if (actEnd && actEnd !== '' && actEnd !== '00:00' && actEnd !== '00:00:00') return false;

    return true;
  }

  get isShiftAssigned(): boolean {
    const shift = this.data.shift;
    return !!(shift && shift.start_time && shift.end_time && shift.start_time !== '' && shift.end_time !== '');
  }

  get showRosterTab(): boolean {
    if (this.isPastDate()) {
      return this.isShiftAssigned;
    }
    return true;
  }

  getInitialEntryReason(shift: any, reasons: any[]): any {
    if (!shift) return null;
    const rawReason = shift.entry_reason;
    const rawReasonNum = rawReason !== null && rawReason !== undefined && rawReason !== '' ? Number(rawReason) : null;
    const otherMatch = (reasons || []).find((r: any) => {
      const desc = (r.description || '').trim().toLowerCase();
      return desc === 'other' || desc === 'others';
    });
    const defaultOtherVal = otherMatch ? otherMatch.id : 'other';

    if (rawReasonNum !== null && rawReasonNum > 0) {
      const match = (reasons || []).find((r: any) => Number(r.id) === rawReasonNum);
      if (match) {
        const desc = (match.description || '').trim().toLowerCase();
        return (desc === 'other' || desc === 'others') ? defaultOtherVal : match.id;
      } else if (shift.entry_reason_others && shift.entry_reason_others.trim() !== '') {
        return defaultOtherVal;
      }
    } else if (shift.entry_reason_others && shift.entry_reason_others.trim() !== '') {
      return defaultOtherVal;
    }
    return null;
  }

  get otherReasonId(): any {
    const match = (this.data.reasons || []).find((r: any) => {
      const desc = (r.description || '').trim().toLowerCase();
      return desc === 'other' || desc === 'others';
    });
    return match ? match.id : 'other';
  }

  get hasOtherInReasons(): boolean {
    return (this.data.reasons || []).some((r: any) => {
      const desc = (r.description || '').trim().toLowerCase();
      return desc === 'other' || desc === 'others';
    });
  }

  isOtherReasonSelected(): boolean {
    const entryReason = this.form?.get('entry_reason')?.value;
    if (entryReason === 'other' || entryReason === '0' || entryReason === 0 || entryReason === -1) {
      return true;
    }
    if (entryReason) {
      const selected = (this.data.reasons || []).find((r: any) => Number(r.id) === Number(entryReason));
      if (selected) {
        const desc = (selected.description || '').trim().toLowerCase();
        return desc === 'others' || desc === 'other' || desc.includes('other');
      }
    }
    const otherText = this.form?.get('entry_reason_others')?.value;
    if (otherText && otherText.trim() !== '') {
      return true;
    }
    return false;
  }

  onCancel() {
    this.dialogRef.close();
  }
}
